import { api } from "./api";

interface CustomBriefingVideoRecord {
  url: string;
  language: string;
  source: string;
  usedFallback: boolean;
  isLocaleSpecific: boolean;
  hasStoredValue: boolean;
  posterUrl?: string;
  captionsUrl?: string;
  transcriptUrl?: string;
}

interface CasegenIdea {
  idea_id: string;
  title: string;
  logline: string;
  summary: string;
  difficulty: number;
  theme: string;
}

interface CasegenIdeasResponse {
  run_id: string;
  city: string;
  indexId: string;
  ideas: CasegenIdea[];
}

interface CasegenStartResponse {
  run_id: string;
  case_id: string;
  status: "queued" | "processing" | "ready";
  message: string;
}

export interface ClubSessionCreatePayload {
  caseIndex: number;
  sessionDate: string;
  sessionStartTime: string;
  timezoneOffsetMinutes: number;
  durationHours: 3 | 6 | 12;
  maxPlayers?: number;
}

export interface ClubSessionCaseAccess {
  caseIndex: number;
  title: string;
  summary: string;
  briefingTitle?: string;
  briefingBody?: string;
  briefingVideoUrl?: string;
  casePayload?: Record<string, unknown> | null;
  evidenceImageAccessMap?: Record<string, string>;
  suspectImageAccessMap?: Record<string, string>;
  sessionHostingAllowed?: boolean;
}

export interface ClubSolveStatus {
  hasSubmittedWarrant: boolean;
  selectedSuspectIndex: number | null;
  selectedSuspectName: string;
  isCorrect: boolean;
  solutionUnlocked: boolean;
  submittedAt?: string | null;
  solutionUnlockedAt?: string | null;
  successfulSolvesCount: number;
  currentRankKey: string;
  currentRankLabel: string;
  nextRankKey: string;
  nextRankLabel: string;
  solvesToNextRank: number;
}

export interface ClubRankLevel {
  key: string;
  label: string;
  minSuccessfulSolves: number;
  sortOrder: number;
  active: boolean;
}

export interface ClubRankConfig {
  configKey: string;
  levels: ClubRankLevel[];
  updatedAt?: string | null;
}

export interface ClubSessionListParams {
  page?: number;
  limit?: number;
  status?: "active" | "closed" | "expired" | "";
  ownerUserId?: string;
}

export interface ClubArchiveEpisodePosition {
  archiveNumber: number;
  archiveTitle: string;
  episodeNumber: number;
  sequenceNumber: number;
  isAuthored: boolean;
  title?: string;
  imageStorageKey?: string;
  imageUrl?: string;
}

export interface ClubArchiveState {
  cadenceDays: number;
  currentEpisode: ClubArchiveEpisodePosition | null;
  currentEpisodeLabel: string;
  nextEpisode: ClubArchiveEpisodePosition | null;
  nextEpisodeLabel: string;
  currentArchiveTitle: string;
  currentArchiveNumber: number;
  plannedEpisodeCount: number;
  authoredEpisodeCount: number;
  scheduledEpisodeCount: number;
  unlockedEpisodeCount: number;
  holdsAtAuthoredEdge: boolean;
  nextUnlockDate: string | null;
  nextUnlockInDays: number;
  nextUnlockLabel: string;
  nextUpdateSummary: string;
  unlockedEpisodes?: ClubArchiveEpisodePosition[];
  archiveDefinitions: Array<{
    archiveNumber: number;
    title: string;
    plannedEpisodeCount: number;
    authoredEpisodeCount: number;
  }>;
}

export interface ClubCustomOrderSummary {
  id: string;
  accessToken: string;
  packageKey: string;
  status: string;
  lifecycleSummary: string;
  title: string;
  selectedIdeaTitle: string;
  city: string;
  location: string;
  language: string;
  firstName: string;
  suspectCount: number;
  generatedIdeasCount: number;
  generatedCaseId: string;
  generationRunId: string;
  ideaRunId: string;
  paymentConfirmedAt: string | null;
  intakeSubmittedAt: string | null;
  ideasGeneratedAt: string | null;
  selectedIdeaAt: string | null;
  deliveryEmailSentAt: string | null;
  intakeEmailSentAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  maskedCustomerEmail: string;
  customerEmail: string;
  openIntakePath: string;
  openCasePath: string;
  canResumeIntake: boolean;
  canOpenCase: boolean;
  coverImageUrl: string;
  generatedContentLanguage?: string;
}

export interface ClubArchiveEpisodePayload {
  archiveNumber: number;
  archiveTitle: string;
  episodeNumber: number;
  title: string;
  excerpt: string;
  bodyMarkdown: string;
  sourceFile: string;
  imageStorageKey?: string;
  imageUrl?: string;
  briefingVideoUrl?: string;
}

export interface ClubArchiveCatalogEntry {
  archiveNumber: number;
  archiveTitle: string;
  episodeNumber: number;
  sourceFile: string;
  sourceRoot?: string;
  imageStorageKey?: string;
  imageUrl?: string;
  briefingVideoUrl?: string;
  detectedTitle?: string;
  detectedExcerpt?: string;
}

export interface ClubArchiveCatalogSummary {
  archiveNumber: number;
  title: string;
  plannedEpisodeCount: number;
  authoredEpisodeCount: number;
}

export interface ClubArchiveSourceFileOption {
  sourceFile: string;
  fileName: string;
}

export interface ClubAdminCustomCaseOrphanPartialDraft {
  caseId: string;
  generatedCaseId: string;
  title: string;
  sourceGeneratedCaseId: string;
  renderStatus: string;
  approvalStatus: string;
  createdAt: string | null;
  updatedAt: string | null;
  referencedStorageKeyCount: number;
  totalObjectCount: number;
  orphanObjectCount: number;
  reasons: string[];
  reasonLabels: string[];
}

export interface ClubAdminCustomCaseOrphanAssetObject {
  storageKey: string;
  sizeBytes: number;
  lastModified: string | null;
}

export interface ClubAdminCustomCaseOrphanAssetGroup {
  generatedCaseId: string;
  caseId: string;
  title: string;
  hasCaseRecord: boolean;
  isPartialDraft: boolean;
  isPublished: boolean;
  totalObjectCount: number;
  referencedObjectCount: number;
  orphanObjectCount: number;
  totalSizeBytes: number;
  orphanSizeBytes: number;
  orphanStorageKeys: string[];
  orphanObjects: ClubAdminCustomCaseOrphanAssetObject[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ClubAdminCustomCaseOrphanAuditResponse {
  ok: true;
  scannedAt: string;
  bucket: string;
  summary: {
    partialDraftCount: number;
    orphanAssetGroupCount: number;
    orphanAssetObjectCount: number;
    totalAgencyObjectCount: number;
    referencedAgencyObjectCount: number;
  };
  partialDrafts: ClubAdminCustomCaseOrphanPartialDraft[];
  orphanAssetGroups: ClubAdminCustomCaseOrphanAssetGroup[];
}

export interface ClubPortraitLibraryMetadata {
  ageBand: string;
  eyeTone: string;
  heightBand: string;
  sex: string;
  hairColor: string;
  hairLength: string;
  skinTone: string;
  tags: string[];
  notes: string;
}

export interface ClubPortraitLibraryRecord {
  _id: string;
  label: string;
  slug: string;
  imagePrompt: string;
  imageStorageKey: string;
  active: boolean;
  metadata: ClubPortraitLibraryMetadata;
  imageAccess?: {
    url: string;
    expiresAt?: number;
    signed?: boolean;
  } | null;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ClubPortraitLibraryEnums {
  AGE_BANDS: string[];
  EYE_TONES: string[];
  HEIGHT_BANDS: string[];
  SEXES: string[];
  HAIR_COLORS: string[];
  HAIR_LENGTHS: string[];
  SKIN_TONES: string[];
}

export type CasegenPortraitSource = "library" | "none";

export interface ClubLifecycleEmailTemplate {
  key: string;
  subject: string;
  body: string;
  ctaLabel: string;
  ctaUrl: string;
  updatedAt?: string;
}

export interface ClubLifecycleEmailEvent {
  _id?: string;
  clubUserId?: string | null;
  email: string;
  templateKey: string;
  eventType: string;
  eventKey: string;
  status: string;
  provider?: string;
  providerId?: string;
  scheduledFor?: string | null;
  sentAt?: string;
  createdAt?: string;
  meta?: Record<string, unknown> | null;
  clubUser?: {
    _id: string;
    displayName: string;
    email: string;
    rank: string;
    paidMonthsCount: number;
    status: string;
  } | null;
}

export interface ClubTrialLifecycleAnalyticsSummary {
  totalTrialRequests: number;
  openTrialProspects: number;
  convertedMembers: number;
  followUpSentCount: number;
  conversionEmailSentCount: number;
  dueForFollowUpNow: number;
  dueForConversionNow: number;
}

export interface ClubTrialLifecycleAnalyticsEntry {
  clubUserId: string;
  displayName: string;
  email: string;
  status: string;
  rank: string;
  paidMonthsCount: number;
  trialRequestedAt?: string | null;
  trialLastSentAt?: string | null;
  trialRequestCount: number;
  daysSinceRequest: number | null;
  convertedToPaid: boolean;
  lastGrantedCaseIndex: number;
  subscription?: {
    status: string;
    currentPeriodEnd?: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  followUpEvent?: {
    sentAt?: string | null;
    providerId?: string;
    eventKey?: string;
  } | null;
  conversionEvent?: {
    sentAt?: string | null;
    providerId?: string;
    eventKey?: string;
  } | null;
}

export interface ClubConversionAnalyticsSummary {
  landingViews: number;
  uniqueLandingVisitors: number;
  trialRequests: number;
  uniqueTrialVisitors: number;
  checkoutStarts: number;
  uniqueCheckoutVisitors: number;
  paidActivations: number;
  uniquePaidMembers: number;
  trialRequestRateFromLanding: string;
  paidActivationRateFromTrial: string;
}

export interface ClubConversionJourney {
  clubUserId: string;
  firstEventAt?: string | null;
  lastEventAt?: string | null;
  landingViewedAt?: string | null;
  trialRequestedAt?: string | null;
  checkoutStartedAt?: string | null;
  membershipActivatedAt?: string | null;
  lastEventType: string;
  clubUser?: {
    _id: string;
    displayName: string;
    email: string;
    status: string;
    rank: string;
    paidMonthsCount: number;
  } | null;
}

type CasegenLlmProvider = "openai" | "deepseek" | "anthropic";

const useCasegenMocks = () => import.meta.env.VITE_CASEGEN_USE_MOCKS === "true";

const buildMockIdeas = (city: string, indexId: string, difficulty = 3, theme = "modern") => {
  const cleanCity = city.trim() || "Unknown City";
  const cleanIndex = indexId.trim() || "idx-0001";
  return {
    run_id: `run_${Date.now()}`,
    city: cleanCity,
    indexId: cleanIndex,
    ideas: [
      {
        idea_id: `idea_${cleanIndex}_1`,
        title: `${cleanCity} Night Shift`,
        logline: `A critical witness disappears after a late-night incident in ${cleanCity}.`,
        summary:
          "A suspicious death at a key workplace triggers an urgent investigation. Detectives must reconcile missing footage, conflicting testimony, and a shifting timeline.",
        difficulty,
        theme,
      },
      {
        idea_id: `idea_${cleanIndex}_2`,
        title: `${cleanCity} Waterfront Silence`,
        logline: `A routine transfer in ${cleanCity} turns into a cover-up when records vanish.`,
        summary:
          "A high-value transfer leads to one unexplained death and several altered records. Investigators follow digital traces and witness statements to uncover motive.",
        difficulty,
        theme,
      },
      {
        idea_id: `idea_${cleanIndex}_3`,
        title: `${cleanCity} Echo Ledger`,
        logline: `An internal audit in ${cleanCity} exposes ties between personal debt and missing evidence.`,
        summary:
          "An internal audit opens a cold case with hidden financial pressure points. Detectives must test alibis against messages, reports, and forensic timing.",
        difficulty,
        theme,
      },
    ],
  } as CasegenIdeasResponse;
};

export const ClubApi = {
  bootstrapMe: async (payload: {
    email: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
  }) => {
    const response = await api.post("/club/me/bootstrap", payload);
    return response.data;
  },
  requestFreeDigitalCase: async (payload: {
    email: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
  }) => {
    const response = await api.post("/club/me/free-digital-case", payload);
    return response.data;
  },
  trackAgencyConversionEvent: async (payload: {
    eventType: "agency_landing_view" | "checkout_started";
    anonymousId: string;
    sourcePath: string;
    occurredAt?: string;
    meta?: Record<string, unknown>;
  }) => {
    const response = await api.post("/club/analytics/conversion-events", payload);
    return response.data as { ok: true; eventId: string };
  },
  testFreeDigitalCaseEmail: async (payload: { email: string }) => {
    const response = await api.post("/club/me/free-digital-case/test", payload);
    return response.data;
  },
  getMe: async () => {
    const response = await api.get("/club/me");
    return response.data as {
      member: {
        rank: string;
        paidMonthsCount: number;
        successfulSolvesCount: number;
        warrantSubmissionsCount: number;
        [key: string]: unknown;
      } | null;
      subscription?: {
        status: string;
        currentPeriodStart?: string | null;
        currentPeriodEnd?: string | null;
        cancelAtPeriodEnd: boolean;
        fastspringProductPath?: string;
      } | null;
      rankProgress?: {
        currentRankKey: string;
        currentRankLabel: string;
        nextRankKey: string;
        nextRankLabel: string;
        solvesToNextRank: number;
        successfulSolvesCount: number;
        levels: ClubRankLevel[];
      };
    };
  },
  getMyCustomOrders: async (
    params: {
      status?: string;
      search?: string;
      limit?: number;
    } = {}
  ) => {
    const response = await api.get("/club/me/custom-orders", { params });
    return response.data as {
      entries: ClubCustomOrderSummary[];
    };
  },
  updateFulfillment: async (payload: {
    fulfillmentMode: "digital" | "physical";
    shippingAddress?: Record<string, string>;
  }) => {
    const response = await api.put("/club/me/fulfillment", payload);
    return response.data;
  },
  getEntitlements: async () => {
    const response = await api.get("/club/me/entitlements");
    return response.data;
  },
  getArchiveState: async () => {
    const response = await api.get("/club/me/archive-state");
    return response.data as { archiveState: ClubArchiveState };
  },
  getArchiveEpisode: async (archiveNumber: number, episodeNumber: number) => {
    const response = await api.get(`/club/me/archive/${archiveNumber}/episodes/${episodeNumber}`);
    return response.data as {
      archiveState: ClubArchiveState;
      episode: ClubArchiveEpisodePayload;
    };
  },
  validateUnlockToken: async (token: string) => {
    const response = await api.post("/club/unlock/validate", { token });
    return response.data;
  },
  getCaseAccess: async (caseIndex: number) => {
    const response = await api.get(`/club/me/cases/${caseIndex}/access`);
    return response.data as ClubSessionCaseAccess & {
      memberRank?: string;
      solveStatus?: ClubSolveStatus;
      access?: {
        granted: boolean;
        mode: string;
        adminOverride?: boolean;
        trialAccess?: boolean;
      };
    };
  },
  submitCaseWarrant: async (caseIndex: number, payload: { suspectIndex: number }) => {
    const response = await api.post(`/club/me/cases/${caseIndex}/warrant`, payload);
    return response.data as {
      ok: boolean;
      caseIndex: number;
      created: boolean;
      solveStatus: ClubSolveStatus;
      warrant: {
        selectedSuspectIndex: number;
        selectedSuspectName: string;
        isCorrect: boolean;
        submittedAt?: string | null;
        solutionUnlockedAt?: string | null;
      };
      promoted: boolean;
      previousRank: string;
      previousRankLabel: string;
      currentRank: string;
      currentRankLabel: string;
    };
  },
  getCasePrintContext: async (
    caseIndex: number,
    params: { theme?: "light" | "dark"; evidence?: string } = {}
  ) => {
    const response = await api.get(`/club/me/cases/${caseIndex}/print-context`, { params });
    return response.data as { printContext: Record<string, unknown> };
  },
  getCasePrintPreview: async (
    caseIndex: number,
    params: { theme?: "light" | "dark"; evidence?: string } = {}
  ) => {
    const response = await api.get(`/club/me/cases/${caseIndex}/print-preview`, { params });
    return response.data as { html: string; meta: { title: string; theme: string; sourceHash: string } };
  },
  downloadCasePdf: async (
    caseIndex: number,
    payload: { theme: "light" | "dark"; evidenceIds: string[] }
  ) => {
    const response = await api.post(`/club/me/cases/${caseIndex}/pdf`, payload, {
      responseType: "blob",
    });
    return response;
  },
  rotateUnlockToken: async () => {
    const response = await api.post("/club/unlock/token/rotate");
    return response.data;
  },
  getUnlockTokenStatus: async () => {
    const response = await api.get("/club/unlock/token/status");
    return response.data;
  },
  adminListMembers: async (params: Record<string, string | number> = {}) => {
    const response = await api.get("/club/admin/members", { params });
    return response.data;
  },
  adminGetMember: async (id: string) => {
    const response = await api.get(`/club/admin/members/${id}`);
    return response.data;
  },
  adminListCases: async (params: Record<string, string | number> = {}) => {
    const response = await api.get("/club/admin/cases", { params });
    return response.data;
  },
  adminListCustomOrders: async (
    params: {
      status?: string;
      search?: string;
      limit?: number;
    } = {}
  ) => {
    const response = await api.get("/club/admin/custom-orders", { params });
    return response.data as {
      ok: true;
      entries: ClubCustomOrderSummary[];
    };
  },
  adminPurgeCustomOrder: async (id: string) => {
    const response = await api.delete(`/club/admin/custom-orders/${id}/purge`);
    return response.data as {
      ok: true;
      deleted: true;
      customOrderId: string;
      generatedCaseId: string;
      deletedOrderCount: number;
      deletedCaseCount: number;
      deletedAssetCount: number;
      deletedGeneratedFallbackPortraitCount: number;
      deletedGeneratedFallbackPortraitAssetCount: number;
      deletedEntitlementCount: number;
      deletedSolveRecordCount: number;
      deletedGameSessionCount: number;
    };
  },
  adminListCustomCaseReviews: async (
    params: {
      status?: "review_required" | "delivery_ready" | "all";
      search?: string;
      limit?: number;
    } = {}
  ) => {
    const response = await api.get("/club/admin/custom-case-reviews", { params });
    return response.data as {
      ok: true;
      entries: Array<{
        caseId: string;
        generatedCaseId: string;
        title: string;
        city: string;
        summary: string;
        updatedAt: string;
        renderStatus: string;
        reviewStatus: string;
        requestedLanguage: string;
        generatedContentLanguage: string;
        localeDriftDetected: boolean;
        briefingVideoUrl: string;
        briefingVideo: CustomBriefingVideoRecord;
        packetReady: boolean;
        customOrderId: string;
        orderStatus: string;
        deliveryEmailSentAt: string;
        customerEmail: string;
        validationHasErrors: boolean;
        validationFindings: Array<{
          severity?: string;
          stage?: string;
          code?: string;
          message?: string;
          evidence_ids?: string[];
          suspect_names?: string[];
          repair_hint?: string;
        }>;
        blockingFindingCount: number;
        warningFindingCount: number;
        deterministicRepairAvailable: boolean;
        repairableFindingCodes: string[];
        repairableFindingCount: number;
        rerunGenerationAvailable: boolean;
        autoReleaseEligible: boolean;
        previewPath: string;
        missingCaseRecord?: boolean;
      }>;
    };
  },
  adminRerunCustomCaseReviewGeneration: async (caseId: string) => {
    const response = await api.post(`/club/admin/custom-case-reviews/${caseId}/rerun-generation`);
    return response.data as {
      ok: true;
      order: Record<string, unknown>;
      queue: {
        runId: string;
        caseId: string;
        status: string;
        webhookTriggered: boolean;
        message: string;
      };
      supersededCaseId: string;
      previousGeneratedCaseId: string;
    };
  },
  adminRevalidateCustomCaseReview: async (caseId: string) => {
    const response = await api.post(`/club/admin/custom-case-reviews/${caseId}/revalidate`);
    return response.data as {
      ok: true;
      released: boolean;
      packetReady: boolean;
      validationHasErrors: boolean;
      blockingFindingCount: number;
      warningFindingCount: number;
      repairedFieldCount: number;
      repairedCodes: string[];
      orderStatus: string;
      deliveryEmailSent: boolean;
      caseId: string;
      generatedCaseId: string;
    };
  },
  adminRenderCustomCaseReviewPacket: async (caseId: string) => {
    const response = await api.post(`/club/admin/custom-case-reviews/${caseId}/render-packet`);
    return response.data as {
      ok: true;
      packetReady: true;
      validationHasErrors: boolean;
      blockingFindingCount: number;
      warningFindingCount: number;
      storageKey: string;
      sizeBytes: number;
      filename: string;
      caseId: string;
      generatedCaseId: string;
    };
  },
  adminReleaseCustomCaseReview: async (caseId: string, payload: { note?: string } = {}) => {
    const response = await api.post(`/club/admin/custom-case-reviews/${caseId}/release`, payload);
    return response.data as {
      ok: true;
      released: true;
      packetReady: boolean;
      validationHasErrors: boolean;
      blockingFindingCount: number;
      warningFindingCount: number;
      orderStatus: string;
      deliveryEmailSent: boolean;
      caseId: string;
      generatedCaseId: string;
    };
  },
  adminListArchiveEpisodes: async () => {
    const response = await api.get("/club/admin/archive-episodes");
    return response.data as {
      entries: ClubArchiveCatalogEntry[];
      archives: ClubArchiveCatalogSummary[];
      availableSourceFiles: ClubArchiveSourceFileOption[];
    };
  },
  adminCreateArchiveEpisode: async (payload: {
    archiveNumber: number;
    archiveTitle: string;
    episodeNumber: number;
    sourceFile: string;
    sourceMarkdown?: string;
    sourceRoot?: string;
    imageStorageKey?: string;
    imageUrl?: string;
    briefingVideoUrl?: string;
  }) => {
    const response = await api.post("/club/admin/archive-episodes", payload);
    return response.data as {
      entry: ClubArchiveCatalogEntry;
      archives: ClubArchiveCatalogSummary[];
      availableSourceFiles: ClubArchiveSourceFileOption[];
    };
  },
  adminGetArchiveEpisode: async (archiveNumber: number, episodeNumber: number) => {
    const response = await api.get(
      `/club/admin/archive-episodes/${archiveNumber}/${episodeNumber}`
    );
    return response.data as {
      entry: ClubArchiveCatalogEntry & { bodyMarkdown?: string };
    };
  },
  adminUpdateArchiveEpisode: async (
    archiveNumber: number,
    episodeNumber: number,
    payload: {
      archiveTitle?: string;
      sourceFile?: string;
      sourceMarkdown?: string;
      sourceRoot?: string;
      imageStorageKey?: string;
      imageUrl?: string;
      briefingVideoUrl?: string;
    }
  ) => {
    const response = await api.patch(
      `/club/admin/archive-episodes/${archiveNumber}/${episodeNumber}`,
      payload
    );
    return response.data as {
      entry: ClubArchiveCatalogEntry;
      archives: ClubArchiveCatalogSummary[];
      availableSourceFiles: ClubArchiveSourceFileOption[];
    };
  },
  adminDeleteArchiveEpisode: async (archiveNumber: number, episodeNumber: number) => {
    const response = await api.delete(
      `/club/admin/archive-episodes/${archiveNumber}/${episodeNumber}`
    );
    return response.data as {
      deletedEntry: ClubArchiveCatalogEntry;
      archives: ClubArchiveCatalogSummary[];
      availableSourceFiles: ClubArchiveSourceFileOption[];
    };
  },
  adminGetCase: async (id: string) => {
    const response = await api.get(`/club/admin/cases/${id}`);
    return response.data;
  },
  adminGetCustomCaseConversionSource: async (
    params: {
      lookup?: string;
      caseId?: string;
      generatedCaseId?: string;
    } = {}
  ) => {
    const response = await api.get("/club/admin/custom-case-conversion/source", { params });
    return response.data as {
      ok: true;
      sourceCase: {
        caseId: string;
        generatedCaseId: string;
        caseIndex: number | null;
        title: string;
        city: string;
        location: string;
        briefingTitle: string;
        summary: string;
        suspectCount: number;
        evidenceCount: number;
        generatedCaseIdSuggestion: string;
        suspects: Array<{
          index: number;
          sourceName: string;
          sourceRelationship: string;
          currentRole: string;
          targetName: string;
          targetRelationship: string;
        }>;
      };
    };
  },
  adminConvertCustomCaseToAgencyCase: async (payload: {
    sourceCaseId?: string;
    sourceGeneratedCaseId?: string;
    targetGeneratedCaseId: string;
    targetTitle: string;
    targetCity: string;
    targetLocation?: string;
    sourceMode?: "dev" | "prod";
    targetMode?: "dev" | "prod";
    suspects: Array<{
      index: number;
      targetName: string;
      targetRelationship: string;
    }>;
  }) => {
    const response = await api.post("/club/admin/custom-case-conversion", payload);
    return response.data as {
      ok: true;
      sourceCase: {
        caseId: string;
        generatedCaseId: string;
        caseIndex: number | null;
        title: string;
        city: string;
        location: string;
        briefingTitle: string;
        summary: string;
        suspectCount: number;
        evidenceCount: number;
        generatedCaseIdSuggestion: string;
        suspects: Array<{
          index: number;
          sourceName: string;
          sourceRelationship: string;
          currentRole: string;
          targetName: string;
          targetRelationship: string;
        }>;
      };
      case: {
        id: string;
        generatedCaseId: string;
        title: string;
        caseIndex: number | null;
        approvalStatus: string;
        renderStatus: string;
        previewPath: string;
      };
      conversionAudit: {
        sourceCaseId: string;
        sourceGeneratedCaseId: string;
        replacementCount: number;
        replacements: Array<{
          from: string;
          to: string;
          label: string;
        }>;
        targetSuspectCount: number;
      };
    };
  },
  adminGetCustomCaseConversionOrphans: async () => {
    const response = await api.get("/club/admin/custom-case-conversion/orphans");
    return response.data as ClubAdminCustomCaseOrphanAuditResponse;
  },
  adminDeleteCustomCaseConversionPartialDraft: async (caseId: string) => {
    const response = await api.delete(`/club/admin/custom-case-conversion/orphans/partial-drafts/${caseId}`);
    return response.data as {
      ok: true;
      deleted: true;
      caseId: string;
      generatedCaseId: string;
      deletedAssetCount: number;
    };
  },
  adminDeleteCustomCaseConversionOrphanAssets: async (generatedCaseId: string) => {
    const response = await api.delete(
      `/club/admin/custom-case-conversion/orphans/assets/${encodeURIComponent(generatedCaseId)}`
    );
    return response.data as {
      ok: true;
      deleted: true;
      generatedCaseId: string;
      deletedAssetCount: number;
    };
  },
  adminGetCasePrintContext: async (
    id: string,
    params: { theme?: "light" | "dark"; evidence?: string } = {}
  ) => {
    const response = await api.get(`/club/admin/cases/${id}/print-context`, { params });
    return response.data as { printContext: Record<string, unknown> };
  },
  adminGetCasePrintPreview: async (
    id: string,
    params: { theme?: "light" | "dark"; evidence?: string } = {}
  ) => {
    const response = await api.get(`/club/admin/cases/${id}/print-preview`, { params });
    return response.data as { html: string; meta: { title: string; theme: string; sourceHash: string } };
  },
  adminDownloadCasePdf: async (
    id: string,
    payload: { theme: "light" | "dark"; evidenceIds: string[] }
  ) => {
    const response = await api.post(`/club/admin/cases/${id}/pdf`, payload, {
      responseType: "blob",
    });
    return response;
  },
  adminExportCaseTransferSnapshot: async (
    id: string,
    params: { sourceMode?: "dev" | "prod"; includePacketPdf?: boolean } = {}
  ) => {
    const response = await api.get(`/club/admin/cases/${id}/transfer-export`, {
      params,
      responseType: "blob",
    });
    return response;
  },
  adminValidateCaseTransferSnapshot: async (payload: {
    bundle: Record<string, unknown>;
    targetMode?: "dev" | "prod";
    preserveGeneratedCaseId?: boolean;
    preserveCaseIndex?: boolean;
    targetGeneratedCaseId?: string;
    targetCaseIndex?: number | null;
    allowOverwriteExisting?: boolean;
    clearPacketPdfAsset?: boolean;
  }) => {
    const response = await api.post("/club/admin/cases/transfer-import/validate", payload);
    return response.data as {
      ok: boolean;
      targetMode: "dev" | "prod";
      validation: {
        ok: boolean;
        identity?: Record<string, unknown> | null;
        sourceCase: Record<string, unknown>;
        assetCount: number;
        malformedAssetEntryCount: number;
        missingBase64AssetCount: number;
        referencedStorageEntryCount?: number;
        packetOnlyAssetCount?: number;
        normalizationSummary?: Record<string, number> | null;
        issues: Array<{
          code: string;
          message: string;
          severity: "error" | "warning";
          conflictingCase?: Record<string, unknown> | null;
        }>;
      };
    };
  },
  adminValidateCaseTransferSnapshotText: async (
    snapshotText: string,
    params: {
      targetMode?: "dev" | "prod";
      preserveGeneratedCaseId?: boolean;
      preserveCaseIndex?: boolean;
      targetGeneratedCaseId?: string;
      targetCaseIndex?: number | null;
      allowOverwriteExisting?: boolean;
      clearPacketPdfAsset?: boolean;
    } = {}
  ) => {
    const response = await api.post("/club/admin/cases/transfer-import/validate-text", snapshotText, {
      params,
      headers: {
        "Content-Type": "text/plain",
      },
    });
    return response.data as {
      ok: boolean;
      targetMode: "dev" | "prod";
      validation: {
        ok: boolean;
        identity?: Record<string, unknown> | null;
        sourceCase: Record<string, unknown>;
        assetCount: number;
        malformedAssetEntryCount: number;
        missingBase64AssetCount: number;
        referencedStorageEntryCount?: number;
        packetOnlyAssetCount?: number;
        normalizationSummary?: Record<string, number> | null;
        issues: Array<{
          code: string;
          message: string;
          severity: "error" | "warning";
          conflictingCase?: Record<string, unknown> | null;
        }>;
      };
    };
  },
  adminImportCaseTransferSnapshot: async (payload: {
    bundle: Record<string, unknown>;
    targetMode?: "dev" | "prod";
    preserveGeneratedCaseId?: boolean;
    preserveCaseIndex?: boolean;
    targetGeneratedCaseId?: string;
    targetCaseIndex?: number | null;
    importAsDraft?: boolean;
    allowPublished?: boolean;
    allowOverwriteExisting?: boolean;
    clearPacketPdfAsset?: boolean;
    skipAssetUploads?: boolean;
  }) => {
    const response = await api.post("/club/admin/cases/transfer-import", payload);
    return response.data as {
      ok: boolean;
      case: Record<string, unknown>;
      uploadedAssetCount: number;
      targetMode: "dev" | "prod";
      importAsDraft: boolean;
      normalizationSummary?: Record<string, number> | null;
      validation?: Record<string, unknown> | null;
      skipAssetUploads?: boolean;
    };
  },
  adminImportCaseTransferSnapshotText: async (
    snapshotText: string,
    params: {
      targetMode?: "dev" | "prod";
      preserveGeneratedCaseId?: boolean;
      preserveCaseIndex?: boolean;
      targetGeneratedCaseId?: string;
      targetCaseIndex?: number | null;
      importAsDraft?: boolean;
      allowPublished?: boolean;
      allowOverwriteExisting?: boolean;
      clearPacketPdfAsset?: boolean;
      skipAssetUploads?: boolean;
    } = {}
  ) => {
    const response = await api.post("/club/admin/cases/transfer-import-text", snapshotText, {
      params,
      headers: {
        "Content-Type": "text/plain",
      },
    });
    return response.data as {
      ok: boolean;
      case: Record<string, unknown>;
      uploadedAssetCount: number;
      targetMode: "dev" | "prod";
      importAsDraft: boolean;
      normalizationSummary?: Record<string, number> | null;
      validation?: Record<string, unknown> | null;
      skipAssetUploads?: boolean;
    };
  },
  adminGetTrialEmailTemplate: async () => {
    const response = await api.get("/club/admin/trial-email-template");
    return response.data;
  },
  adminGetLifecycleEmailTemplates: async () => {
    const response = await api.get("/club/admin/lifecycle-email-templates");
    return response.data;
  },
  adminGetLifecycleEmailTemplate: async (templateKey: string) => {
    const response = await api.get(`/club/admin/lifecycle-email-templates/${templateKey}`);
    return response.data;
  },
  adminListLifecycleEmailEvents: async (
    params: {
      limit?: number;
      templateKey?: string;
      eventType?: string;
      email?: string;
      status?: string;
    } = {}
  ) => {
    const response = await api.get("/club/admin/lifecycle-email-events", { params });
    return response.data as {
      events: ClubLifecycleEmailEvent[];
      filters: {
        limit: number;
        templateKey: string;
        eventType: string;
        email: string;
        status: string;
      };
      allowedTemplateKeys: string[];
      allowedEventTypes: string[];
    };
  },
  adminGetLifecycleEmailAnalytics: async (
    params: {
      recentLimit?: number;
      now?: string;
      requestedFrom?: string;
      requestedTo?: string;
      conversionState?: "all" | "open" | "converted";
    } = {}
  ) => {
    const response = await api.get("/club/admin/lifecycle-email-analytics", { params });
    return response.data as {
      summary: ClubTrialLifecycleAnalyticsSummary;
      recentTrials: ClubTrialLifecycleAnalyticsEntry[];
      generatedAt: string;
      filters: {
        recentLimit: number;
        now: string;
        requestedFrom?: string | null;
        requestedTo?: string | null;
        conversionState: "all" | "open" | "converted";
      };
    };
  },
  adminGetConversionAnalytics: async (
    params: {
      recentLimit?: number;
      eventFrom?: string;
      eventTo?: string;
    } = {}
  ) => {
    const response = await api.get("/club/admin/conversion-analytics", { params });
    return response.data as {
      summary: ClubConversionAnalyticsSummary;
      recentJourneys: ClubConversionJourney[];
      recentEvents: Array<Record<string, unknown>>;
      filters: {
        recentLimit: number;
        eventFrom?: string | null;
        eventTo?: string | null;
      };
    };
  },
  adminUpdateLifecycleEmailTemplate: async (
    templateKey: string,
    payload: {
      subject: string;
      body: string;
      ctaLabel: string;
      ctaUrl: string;
    }
  ) => {
    const response = await api.put(`/club/admin/lifecycle-email-templates/${templateKey}`, payload);
    return response.data;
  },
  adminRunLifecycleEmailSweep: async (payload: { now?: string; dashboardUrl?: string } = {}) => {
    const response = await api.post("/club/admin/lifecycle-email/run-sweep", payload);
    return response.data;
  },
  adminSendTestLifecycleEmail: async (payload: {
    templateKey: string;
    to: string;
    displayName?: string;
    dashboardUrl?: string;
    paidMonthsCount?: number;
    rank?: string;
    lastGrantedCaseIndex?: number;
    currentArchiveLabel?: string;
    nextArchiveLabel?: string;
    nextArchiveDropDate?: string;
    previousRankLabel?: string;
    nextRankLabel?: string;
    nextRankReviewDate?: string;
    accessEndsOnDate?: string;
    accessChangeReason?: string;
  }) => {
    const response = await api.post("/club/admin/lifecycle-email/send-test", payload);
    return response.data;
  },
  adminUpdateTrialEmailTemplate: async (payload: {
    subject: string;
    body: string;
    ctaLabel: string;
    ctaUrl: string;
  }) => {
    const response = await api.put("/club/admin/trial-email-template", payload);
    return response.data;
  },
  adminSendTestTrialEmail: async (payload: {
    to: string;
    displayName?: string;
    trialCaseIndex?: number;
    freeCaseUrl?: string;
    joinUrl?: string;
  }) => {
    const response = await api.post("/club/admin/trial-email/send-test", payload);
    return response.data;
  },
  adminPatchMember: async (id: string, payload: Record<string, unknown>) => {
    const response = await api.patch(`/club/admin/members/${id}`, payload);
    return response.data;
  },
  adminGetRankConfig: async () => {
    const response = await api.get("/club/admin/rank-config");
    return response.data as { config: ClubRankConfig };
  },
  adminUpdateRankConfig: async (payload: { levels: ClubRankLevel[] }) => {
    const response = await api.put("/club/admin/rank-config", payload);
    return response.data as { ok: true; config: ClubRankConfig };
  },
  adminUpdateCaseSolveConfig: async (id: string, payload: { correctSuspectIndex: number }) => {
    const response = await api.patch(`/club/admin/cases/${id}/solve-config`, payload);
    return response.data as {
      ok: true;
      solveConfig: {
        correctSuspectIndex: number;
        inferredCorrectSuspectIndex: number;
      };
    };
  },
  adminSyncEntitlementsFromFields: async (
    id: string,
    payload: {
      includeUnpublished?: boolean;
      cleanupLegacyEntitlements?: boolean;
      // Legacy compatibility: deprecated in paid-progression sync mode.
      upToCaseIndex?: number;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/members/${id}/entitlements/sync-from-fields`,
      payload
    );
    return response.data;
  },
  adminSimulateMemberPayments: async (
    id: string,
    payload: {
      paymentCount?: number;
      includeUnpublished?: boolean;
    } = {}
  ) => {
    const response = await api.post(`/club/admin/members/${id}/simulate-payments`, payload);
    return response.data;
  },
  adminSimulateMemberBillingEvent: async (
    id: string,
    payload: {
      eventType: "charge_failed" | "reactivated" | "cancelled" | "expired";
    }
  ) => {
    const response = await api.post(`/club/admin/members/${id}/simulate-billing-event`, payload);
    return response.data;
  },
  adminResetMemberTestState: async (id: string) => {
    const response = await api.post(`/club/admin/members/${id}/reset-test-state`);
    return response.data;
  },
  adminDeleteTestMember: async (id: string) => {
    const response = await api.post(`/club/admin/members/${id}/delete-test-member`);
    return response.data;
  },
  adminGetUploadUrl: async (payload: {
    caseIndex?: number;
    generatedCaseId?: string;
    archiveNumber?: number;
    episodeNumber?: number;
    portraitLibraryId?: string;
    evidenceId?: string;
    suspectKey?: string;
    filename: string;
    contentType: string;
    sizeBytes?: number;
  }) => {
    const response = await api.post("/club/admin/assets/upload-url", payload);
    return response.data as { uploadUrl: string; storageKey: string; expiresIn: number };
  },
  adminGetAssetAccessUrl: async (payload: { storageKey: string }) => {
    const response = await api.post("/club/admin/assets/access-url", payload);
    return response.data as { url: string; expiresAt?: number; signed?: boolean };
  },
  adminGetPortraitLibraryEnums: async () => {
    const response = await api.get("/club/admin/portrait-library/enums");
    return response.data as { ok: true; enums: ClubPortraitLibraryEnums };
  },
  adminListPortraitLibrary: async (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      active?: "true" | "false" | "";
      hasImage?: "true" | "false" | "";
    } = {}
  ) => {
    const response = await api.get("/club/admin/portrait-library", { params });
    return response.data as {
      ok: true;
      portraits: ClubPortraitLibraryRecord[];
      total: number;
      pages: number;
      currentPage: number;
    };
  },
  adminCreatePortraitRecord: async (payload: {
    label: string;
    slug?: string;
    imagePrompt?: string;
    imageStorageKey?: string;
    active?: boolean;
    metadata?: Partial<ClubPortraitLibraryMetadata>;
  }) => {
    const response = await api.post("/club/admin/portrait-library", payload);
    return response.data as { ok: true; portrait: ClubPortraitLibraryRecord };
  },
  adminGetPortraitRecord: async (id: string) => {
    const response = await api.get(`/club/admin/portrait-library/${id}`);
    return response.data as { ok: true; portrait: ClubPortraitLibraryRecord };
  },
  adminUpdatePortraitRecord: async (
    id: string,
    payload: {
      label?: string;
      slug?: string;
      imagePrompt?: string;
      imageStorageKey?: string;
      active?: boolean;
      metadata?: Partial<ClubPortraitLibraryMetadata>;
    }
  ) => {
    const response = await api.patch(`/club/admin/portrait-library/${id}`, payload);
    return response.data as { ok: true; portrait: ClubPortraitLibraryRecord };
  },
  adminDeletePortraitRecord: async (id: string) => {
    const response = await api.delete(`/club/admin/portrait-library/${id}`);
    return response.data as { ok: true; deleted: boolean; portraitId: string };
  },
  adminCreateCase: async (payload: Record<string, unknown>) => {
    const response = await api.post("/club/admin/cases", payload);
    return response.data;
  },
  adminUpdateCase: async (id: string, payload: Record<string, unknown>) => {
    const response = await api.patch(`/club/admin/cases/${id}`, payload);
    return response.data;
  },
  adminRunGeneratedCaseDeterministicRepair: async (id: string) => {
    const response = await api.post(`/club/admin/cases/${id}/deterministic-repair`);
    return response.data as {
      ok: true;
      caseId: string;
      generatedCaseId: string;
      caseOrigin: string;
      packetReady: boolean;
      packetRendered: boolean;
      packetRenderError: string | null;
      qualityScore: number;
      validationHasErrors: boolean;
      blockingFindingCount: number;
      warningFindingCount: number;
      repairedFieldCount: number;
      repairedCodes: string[];
      orderStatus: string;
      deliveryEmailSent: boolean;
      repairPath: string;
    };
  },
  adminEmailGeneratedCaseRepairReport: async (id: string) => {
    const response = await api.post(`/club/admin/cases/${id}/email-repair-report`);
    return response.data as {
      ok: true;
      caseId: string;
      generatedCaseId: string;
      caseOrigin: string;
      email: string;
      sent: boolean;
      skipped: boolean;
      reason: string;
      qualityScore: number;
      validationHasErrors: boolean;
      blockingFindingCount: number;
      warningFindingCount: number;
      repairPath: string;
    };
  },
  adminGenerateBatch: async (payload: { batchMonth: string; mode: "digital" | "physical" }) => {
    const response = await api.post("/club/admin/fulfillment/batches/generate", payload);
    return response.data;
  },
  adminExportBatch: async (batchId: string) => {
    const response = await api.post(`/club/admin/fulfillment/batches/${batchId}/export`);
    return response.data;
  },
  adminCasegenIdeas: async (payload: {
    city: string;
    indexId: string;
    difficulty?: number;
    theme?: string;
    llm_provider?: CasegenLlmProvider;
  }) => {
    if (useCasegenMocks()) {
      return buildMockIdeas(
        payload.city,
        payload.indexId,
        Number(payload.difficulty || 3),
        payload.theme || "modern"
      );
    }
    const response = await api.post("/club/admin/casegen/ideas", payload);
    return response.data as CasegenIdeasResponse;
  },
  adminCasegenStartFromIdea: async (payload: {
    city: string;
    indexId: string;
    selected_idea: CasegenIdea;
    difficulty?: number;
    theme?: string;
    mode?: "draft";
    llm_provider?: CasegenLlmProvider;
    portrait_source?: CasegenPortraitSource;
  }) => {
    if (useCasegenMocks()) {
      return {
        run_id: `run_${Date.now()}`,
        case_id: `case_${payload.indexId}_${payload.selected_idea.idea_id}`,
        status: "queued",
        message: "Mock generation started. Draft will be ready for review.",
      } as CasegenStartResponse;
    }
    const response = await api.post("/club/admin/casegen/start", {
      ...payload,
      mode: payload.mode || "draft",
    });
    return response.data as CasegenStartResponse;
  },
  adminApproveGeneratedCase: async (id: string) => {
    const response = await api.post(`/club/admin/casegen/${id}/approve`);
    return response.data;
  },
  adminTransitionGeneratedCaseApproval: async (
    id: string,
    payload: {
      targetStatus:
        | "draft_generated"
        | "under_review"
        | "needs_edits"
        | "approved"
        | "published_ready"
        | "published";
      caseIndex?: number;
      notes?: string;
      reasons?: string[];
      checklist?: Record<string, unknown>;
    }
  ) => {
    const response = await api.post(`/club/admin/casegen/${id}/approval-transition`, payload);
    return response.data;
  },
  adminPromoteGeneratedToFreeCase: async (
    id: string,
    payload: {
      notes?: string;
    } = {}
  ) => {
    const response = await api.post(`/club/admin/casegen/${id}/promote-free-case`, payload);
    return response.data;
  },
  adminUnapproveGeneratedCase: async (id: string) => {
    const response = await api.post(`/club/admin/casegen/${id}/unapprove`);
    return response.data;
  },
  adminDeleteGeneratedCase: async (id: string) => {
    const response = await api.delete(`/club/admin/casegen/${id}`);
    return response.data as {
      ok: true;
      deleted: true;
      case_id: string;
      deletedAssetCount: number;
      preservedPortraitKeyCount: number;
      preservedExternalKeyCount: number;
    };
  },
  adminDeleteCase: async (id: string) => {
    const response = await api.delete(`/club/admin/cases/${id}`);
    return response.data;
  },
  adminUpdateGeneratedEvidence: async (
    id: string,
    evidenceId: string,
    payload: {
      title?: string;
      date?: string;
      source?: string;
      confidentiality?: string;
      sections?: Array<{ heading: string; content_markdown: string }>;
      images?: Array<string | { storageKey: string; description?: string }>;
      clue_tags?: string[];
      image_prompt?: string;
    }
  ) => {
    const response = await api.patch(`/club/admin/casegen/${id}/evidence/${evidenceId}`, payload);
    return response.data;
  },
  adminGenerateAutopsyEvidence: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-autopsy`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      autopsyFields: Record<string, string>;
      sections: Array<{ heading: string; content_markdown: string }>;
      generatedFields: Record<string, string>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminGenerateVictimProfile: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-victim-profile`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      victimProfileFields: Record<string, string>;
      sections: Array<{ heading: string; content_markdown: string }>;
      image_prompt: string;
      generatedFields: Record<string, string>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminGenerateWitnessStatements: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-witness-statements`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      statements?: Array<{
        heading: string;
        statement: string;
        date?: string;
      }>;
      sections: Array<{ heading: string; content_markdown: string }>;
      generatedFields: Record<string, unknown>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminGenerateTimeline: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-timeline`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      sections: Array<{ heading: string; content_markdown: string }>;
      generatedFields: Record<string, unknown>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminGenerateSuspectTexts: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-suspect-texts`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      chains?: Array<{
        heading: string;
        messages: Array<{ name: string; message: string; timestamp?: string }>;
      }>;
      sections: Array<{ heading: string; content_markdown: string }>;
      generatedFields: Record<string, unknown>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminGenerateSuspectEmails: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-suspect-emails`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      emails?: Array<{
        heading: string;
        from: string;
        to: string;
        cc?: string;
        date: string;
        subject: string;
        body: string;
      }>;
      sections: Array<{ heading: string; content_markdown: string }>;
      generatedFields: Record<string, unknown>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminGenerateNewspaperArticles: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-newspaper-articles`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      articles?: Array<{
        heading: string;
        headline: string;
        byline: string;
        publication: string;
        date: string;
        article: string;
      }>;
      sections: Array<{ heading: string; content_markdown: string }>;
      generatedFields: Record<string, unknown>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminGeneratePoliceReport: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-police-report`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      report_fields?: Record<string, string>;
      narrative_sections?: Array<{ heading: string; content_markdown: string }>;
      sections: Array<{ heading: string; content_markdown: string }>;
      generatedFields: Record<string, unknown>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminGenerateCaseBackground: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
      image_prompt_only?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-case-background`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      sections: Array<{ heading: string; content_markdown: string }>;
      image_prompt: string;
      generatedFields: Record<string, unknown>;
      llm_provider: string;
      overwrite_existing: boolean;
      image_prompt_only?: boolean;
    };
  },
  adminGenerateAdditionalEvidence: async (
    id: string,
    evidenceId: string,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/evidence/${evidenceId}/generate-additional-evidence`,
      payload
    );
    return response.data as {
      ok: true;
      evidenceId: string;
      sections: Array<{ heading: string; content_markdown: string }>;
      image_prompt: string;
      generatedFields: Record<string, unknown>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  adminUpdateGeneratedContent: async (
    id: string,
    payload: {
      title?: string;
      summary?: string;
      logline?: string;
      news_script?: string;
      briefing_body?: string;
      briefingVideoUrl?: string;
    }
  ) => {
    const response = await api.patch(`/club/admin/casegen/${id}/content`, payload);
    return response.data;
  },
  adminUpdateGeneratedSuspect: async (
    id: string,
    suspectIndex: number,
    payload: {
      name?: string;
      relation?: string;
      role?: string;
      motive?: string;
      age?: string;
      sex?: string;
      hair?: string;
      eyes?: string;
      height?: string;
      build?: string;
      marital_status?: string;
      occupation?: string;
      employer?: string;
      alibi?: string;
      evidence_against?: string;
      evidence_in_favor?: string;
      opportunity?: string;
      behavior_notes?: string;
      image_prompt?: string;
      image_storage_key?: string;
      image_url?: string;
    }
  ) => {
    const response = await api.patch(`/club/admin/casegen/${id}/suspect/${suspectIndex}`, payload);
    return response.data;
  },
  adminGenerateSuspectEvidence: async (
    id: string,
    suspectIndex: number,
    payload: {
      llm_provider?: CasegenLlmProvider;
      overwrite_existing?: boolean;
    } = {}
  ) => {
    const response = await api.post(
      `/club/admin/casegen/${id}/suspect/${suspectIndex}/generate-evidence`,
      payload
    );
    return response.data as {
      ok: true;
      suspectIndex: number;
      suspect: Record<string, unknown>;
      generatedFields: Record<string, string>;
      llm_provider: string;
      overwrite_existing: boolean;
    };
  },
  createGroupSession: async (payload: ClubSessionCreatePayload) => {
    const response = await api.post("/club/sessions", payload);
    return response.data as {
      session: {
        id: string;
        caseIndex: number;
        caseName: string;
        startsAt: string;
        expiresAt: string;
        durationHours: number;
        maxPlayers: number;
        status: string;
      };
      guestUrl: string;
      token: string;
      inviteText: string;
    };
  },
  getGroupSessionPublicByToken: async (token: string) => {
    const response = await api.get(`/club/sessions/${encodeURIComponent(token)}/public`);
    return response.data as {
      ok: boolean;
      session: {
        sessionId: string;
        caseIndex: number;
        caseName: string;
        startsAt: string;
        expiresAt: string;
        durationHours: number;
        status: string;
        participantCount: number;
        maxPlayers: number;
        isFull: boolean;
        hostFirstName: string;
      };
    };
  },
  joinGroupSessionByToken: async (
    token: string,
    payload: { displayName: string; clientFingerprint?: string }
  ) => {
    const response = await api.post(`/club/sessions/${encodeURIComponent(token)}/join`, payload);
    return response.data as {
      ok: boolean;
      session: {
        sessionId: string;
        caseIndex: number;
        caseName: string;
        startsAt: string;
        expiresAt: string;
        durationHours: number;
        status: string;
        participantCount: number;
        maxPlayers: number;
        isFull: boolean;
        hostFirstName: string;
        joinedDisplayName: string;
      };
      caseAccess: ClubSessionCaseAccess;
    };
  },
  validateGroupSessionWarrant: async (token: string, payload: { suspectIndex: number }) => {
    const response = await api.post(`/club/sessions/${encodeURIComponent(token)}/warrant`, payload);
    return response.data as {
      ok: boolean;
      caseIndex: number;
      warrant: {
        selectedSuspectIndex: number;
        selectedSuspectName: string;
        isCorrect: boolean;
      };
      solutionUnlocked: boolean;
    };
  },
  getGroupSessionOwnerStatus: async (sessionId: string) => {
    const response = await api.get(`/club/sessions/${sessionId}`);
    return response.data as {
      ok: boolean;
      session: {
        id: string;
        caseIndex: number;
        caseName: string;
        startsAt: string;
        expiresAt: string;
        durationHours: number;
        maxPlayers: number;
        status: string;
        participants: Array<{ displayName: string; joinedAt: string }>;
      };
    };
  },
  listGroupSessions: async (params: ClubSessionListParams = {}) => {
    const response = await api.get("/club/sessions", { params });
    return response.data as {
      ok: boolean;
      sessions: Array<{
        id: string;
        caseIndex: number;
        caseName: string;
        ownerUserId: string;
        ownerFirstName: string;
        startsAt: string;
        expiresAt: string;
        durationHours: number;
        maxPlayers: number;
        participantCount: number;
        participants: Array<{ displayName: string; joinedAt?: string | null }>;
        status: "active" | "closed" | "expired";
        closedAt?: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  },
  closeGroupSession: async (sessionId: string) => {
    const response = await api.post(`/club/sessions/${sessionId}/close`);
    return response.data as {
      ok: boolean;
      session: { id: string; status: string; closedAt?: string };
    };
  },
  deleteGroupSession: async (sessionId: string) => {
    const response = await api.delete(`/club/sessions/${sessionId}`);
    return response.data as {
      ok: boolean;
      deletedSessionId: string;
    };
  },
};
